import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';

const STORAGE_KEY = 'recentPaymentMethods';
const MAX_RECENT = 5;

function getRecentMethods() {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
        return [];
    }
}

function saveRecentMethod(method) {
    const entry = { slug: method.slug, name: method.name };
    const current = getRecentMethods();
    const filtered = current.filter((m) => m.slug !== entry.slug);
    const updated = [entry, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return updated;
}

function PaymentMethodList({ paymentMethods, onPick, t }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [recentMethods, setRecentMethods] = useState(getRecentMethods);

    const handlePick = (method) => {
        const updated = saveRecentMethod(method);
        setRecentMethods(updated);
        onPick(method);
    };

    const validSlugs = new Set(paymentMethods.map((m) => m.slug));
    const visibleRecent = recentMethods.filter((m) => validSlugs.has(m.slug));

    const filteredMethods = paymentMethods.filter((method) =>
        method.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return (
        <Box>
            {/* ── Recent quick-select chips ── */}
            {visibleRecent.length > 0 && (
                <Box mb={1.5}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.75, display: 'block' }}
                    >
                        {t('common.recentPaymentMethods')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {visibleRecent.map((method) => (
                            <Chip
                                key={method.slug}
                                label={method.name}
                                onClick={() =>
                                    handlePick(paymentMethods.find((m) => m.slug === method.slug))
                                }
                                color="primary"
                                variant="outlined"
                                size="small"
                                clickable
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* ── Search bar ── */}
            <Box mb={2}>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder={t('common.searchOrSelectPaymentMethod')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                />
            </Box>

            {/* ── Full method list ── */}
            <Box
                sx={{
                    maxHeight: 200,
                    overflowY: 'auto',
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                }}
            >
                <List dense disablePadding>
                    {filteredMethods.length === 0 && (
                        <ListItem sx={{ bgcolor: 'grey.50' }}>
                            <ListItemText
                                sx={{ fontStyle: 'italic' }}
                                primary={t('common.noResults') || 'No methods found.'}
                            />
                        </ListItem>
                    )}
                    {filteredMethods.map((method) => (
                        <ListItem
                            key={method.slug}
                            divider
                            sx={{ py: 1, pl: 2, pr: 1, gap: 1, bgcolor: 'grey.50' }}
                        >
                            {/* Flex row instead of secondaryAction so long names wrap
                                beside the button rather than running underneath it. */}
                            <ListItemText primary={method.name} sx={{ my: 0, pr: 1 }} />
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => handlePick(method)}
                                sx={{ flexShrink: 0 }}
                            >
                                {t('common.pay', 'Pay')}
                            </Button>
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Box>
    );
}

export default PaymentMethodList;
